import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as HighchartsExporting from 'highcharts/modules/exporting';

// Initialize exporting module safely
if (typeof HighchartsExporting === 'function') {
    HighchartsExporting(Highcharts);
} else if (HighchartsExporting && typeof HighchartsExporting.default === 'function') {
    HighchartsExporting.default(Highcharts);
}
import api from '../utils/api';
import { FiX, FiAlertCircle, FiChevronDown, FiDownload, FiCalendar } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const COLORS = [
    '#FF6B6B', '#FF9F43', '#FECA57', '#48DBFB', '#FF9FF3',
    '#54A0FF', '#5F27CD', '#00D2D3', '#1DD1A1', '#EE5A24',
    '#009432', '#0652DD', '#9980FA', '#ED4C67', '#F79F1F',
    '#A3CB38', '#1289A7', '#D980FA', '#FDA7DF', '#C8D6E5',
];

// Format date as YYYY-MM-DD for input[type=date] using local time
const toInputDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
};

const MissingOutPunchChart = ({ isHROrAdmin }) => {
    const now = new Date();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [rawData, setRawData] = useState([]);
    const [chartOptions, setChartOptions] = useState(null);
    const [modal, setModal] = useState({ show: false, name: '', dates: [], count: 0 });

    useEffect(() => {
        if (isHROrAdmin) fetchData();
    }, [fromDate, toDate, isHROrAdmin]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/hrms/missing-out-punch', {
                params: {
                    from: fromDate || undefined,
                    to: toDate || undefined
                }
            });

            const list = res.data?.data || [];

            // Synchronize dates from backend (handles holiday skipping)
            if (res.data?.from) setFromDate(res.data.from);
            if (res.data?.to) setToDate(res.data.to);

            setRawData(list);
            buildChart(list);
        } catch (err) {
            console.error('MissingOutPunchChart error:', err);
            setRawData([]);
            setChartOptions(null);
        } finally {
            setLoading(false);
        }
    };

    const buildChart = (list) => {
        if (!list.length) { setChartOptions(null); return; }

        const shortName = (name) => {
            const parts = name.trim().split(' ');
            return parts.length >= 2 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
        };

        // Reduced height per item to 25px (was 35px) to tighten spacing
        const dynamicHeight = Math.max(320, list.length * 25 + 60);

        setChartOptions({
            exporting: {
                enabled: true,
                sourceWidth: 1200,
                chartOptions: {
                    chart: { backgroundColor: '#ffffff' },
                    title: {
                        text: 'Missing Out Punch  ' + filterLabel,
                        style: { fontSize: '20px', fontWeight: 'bold', color: '#1f2937', fontFamily: 'Arial' }
                    }
                }
            },
            chart: {
                type: 'bar',
                height: dynamicHeight,
                style: { fontFamily: 'Inter, sans-serif' },
                spacing: [8, 16, 8, 8],
                animation: { duration: 600 },
            },
            title: { text: null },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                categories: list.map(e => shortName(e.name)),
                lineWidth: 0,
                tickWidth: 0,
                labels: { style: { color: '#374151', fontSize: '11px', fontWeight: '500' } },
            },
            yAxis: {
                title: { text: null },
                gridLineColor: '#f1f5f9',
                labels: {
                    style: { color: '#94a3b8', fontSize: '10px' },
                    formatter: function () { return Number.isInteger(this.value) ? this.value : ''; },
                },
                allowDecimals: false,
                min: 0,
            },
            tooltip: {
                useHTML: true,
                borderRadius: 10,
                borderWidth: 0,
                backgroundColor: 'rgba(255,255,255,0.97)',
                shadow: true,
                formatter: function () {
                    const c = this.point.custom;
                    return `<div style="padding:8px 12px;font-size:12px">
                                <b style="color:#1e293b">${c.name}</b><br/>
                                <span style="color:#dc2626">⚠ <b>${c.count}</b> missing out punch${c.count > 1 ? 'es' : ''}</span><br/>
                                <span style="color:#94a3b8;font-size:11px">Click to see dates</span>
                            </div>`;
                },
            },
            plotOptions: {
                bar: {
                    borderRadius: 5,
                    dataLabels: {
                        enabled: true,
                        format: '{y}',
                        style: { fontSize: '10px', fontWeight: '700', textOutline: 'none', color: '#374151' },
                    },
                    cursor: 'pointer',
                    colorByPoint: true,
                    colors: COLORS,
                    point: {
                        events: {
                            click: function () {
                                const c = this.custom;
                                setModal({ show: true, name: c.name, dates: c.dates || [], count: c.count });
                            }
                        }
                    },
                    states: { hover: { brightness: 0.08 } },
                }
            },
            series: [{
                name: 'Missing Out Punches',
                data: list.map(e => ({
                    y: e.count,
                    custom: { name: e.name, count: e.count, dates: e.dates },
                })),
            }],
        });
    };

    // ── Excel Export (same pattern as MissingStatusReport) ───────────────────
    const handleExport = async () => {
        if (!rawData.length) return;
        setExporting(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Missing Out Punch');

            // 1. Company Name (Row 1)
            worksheet.mergeCells('A1:D1');
            const companyCell = worksheet.getCell('A1');
            companyCell.value = 'Enova Software and Hardware Solutions P Ltd.';
            companyCell.font = { name: 'Calibri', size: 18, bold: true };
            companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(1).height = 35; // Increased height
            worksheet.getRow(2).height = 25;

            // 2. Report Title (Row 2) - Red Header
            worksheet.mergeCells('A2:D2');
            const titleCell = worksheet.getCell('A2');
            titleCell.value = `Missing Out Punch Report — ${fromDate} to ${toDate}`;
            titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; // White text
            titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } }; // Dark Red background
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

            // 2. Table Headers (Dark Blue)
            const headerRow = worksheet.addRow(['#', 'Employee Name', 'Missing Days', 'Dates']);
            headerRow.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } }; // White text
            headerRow.height = 25;

            ['A', 'B', 'C', 'D'].forEach(col => {
                const cell = worksheet.getCell(`${col}3`);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232F3E' } }; // Dark Blue/Black
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                if (col === 'B' || col === 'D') cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
            });

            // 3. Data Rows with Zebra Striping
            let totalMisses = 0;
            rawData.forEach((emp, index) => {
                const dates = (emp.dates || []).join(', ');
                const row = worksheet.addRow([index + 1, emp.name, emp.count, dates]);

                totalMisses += emp.count;

                // Alternating Row Color (Light Pink)
                if (index % 2 !== 0) {
                    row.eachCell((cell) => {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEB' } }; // Light Pink
                    });
                }

                // Styling
                row.height = 30; // Taller rows
                row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }; // Index
                row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }; // Name
                row.getCell(2).font = { name: 'Calibri', size: 11 };

                // Count - Red & Bold
                const countCell = row.getCell(3);
                countCell.value = emp.count;
                countCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFF0000' } }; // Red
                countCell.alignment = { horizontal: 'center', vertical: 'middle' };

                // Dates
                row.getCell(4).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true, indent: 1 };
                row.getCell(4).font = { name: 'Calibri', size: 10 };
            });

            // 4. Total Row
            const totalRow = worksheet.addRow(['', 'TOTAL', totalMisses, '']);
            totalRow.height = 25;

            // Style Total Row (Dark Blue like header)
            ['A', 'B', 'C', 'D'].forEach(col => {
                const cell = totalRow.getCell(col);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232F3E' } };
                cell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            // Merge A & B for "TOTAL" text
            const trNum = totalRow.number;
            worksheet.mergeCells(`A${trNum}:B${trNum}`);
            totalRow.getCell(1).value = 'TOTAL';

            // 5. Borders for entire table
            const lastRow = totalRow.number;
            for (let r = 3; r <= lastRow; r++) {
                const row = worksheet.getRow(r);
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFDDDDDD' } }, // Light gray borders
                        bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } }
                    };
                });
            }

            // 6. Column Widths
            worksheet.getColumn(1).width = 10; // Index
            worksheet.getColumn(2).width = 30; // Name
            worksheet.getColumn(3).width = 15; // Count
            worksheet.getColumn(4).width = 80; // Dates

            // Write File
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Missing_Out_Punch_Report_${fromDate}_${toDate}.xlsx`);
        } catch (err) {
            console.error('Export error:', err);
        } finally {
            setExporting(false);

        }
    };


    const filterLabel = `${formatDateDDMMYYYY(fromDate)}  →  ${formatDateDDMMYYYY(toDate)}`;

    return (
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition flex flex-col" style={{ height: '460px' }}>

            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-gray-100 flex-shrink-0">
                <div className="min-w-0 flex-1 mr-3">
                    <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                        <FiAlertCircle className="text-red-500 flex-shrink-0" />
                        <span className="truncate">Missing Out Punch {filterLabel}</span>
                    </h3>
                    {/* <p className="text-xs text-gray-400 mt-0.5 truncate">{filterLabel}</p> */}
                </div>

                {/* Export button */}
                <button
                    onClick={handleExport}
                    disabled={exporting || !rawData.length}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition shadow-sm"
                >
                    <FiDownload size={13} />
                    {exporting ? 'Exporting…' : 'Export'}
                </button>
            </div>

            {/* ── Date filters ── */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-5 py-2 border-b border-gray-50 flex-shrink-0">
                <FiCalendar className="text-gray-400 text-sm flex-shrink-0" />
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <label className="text-xs text-gray-500 font-medium">From</label>
                    <input
                        type="date"
                        value={fromDate}
                        max={toDate}
                        onChange={e => setFromDate(e.target.value)}
                        className="text-xs text-gray-700 font-medium bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 outline-none cursor-pointer hover:border-blue-400 transition"
                    />
                </div>
                <span className="text-gray-300 text-sm flex-shrink-0">→</span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <label className="text-xs text-gray-500 font-medium">To</label>
                    <input
                        type="date"
                        value={toDate}
                        min={fromDate}
                        max={toInputDate(new Date(Date.now() - 86400000))}
                        onChange={e => setToDate(e.target.value)}
                        className="text-xs text-gray-700 font-medium bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 outline-none cursor-pointer hover:border-blue-400 transition"
                    />
                </div>
                {/* Presets — ml-auto keeps them right on desktop; flex-wrap pushes them to next line on mobile */}
                <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                    {[
                        {
                            label: 'This Month', fn: () => {
                                setFromDate(toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)));
                                setToDate(toInputDate(new Date(Date.now() - 86400000)));
                            }
                        },
                        {
                            label: 'Last Month', fn: () => {
                                const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                                const t = new Date(now.getFullYear(), now.getMonth(), 0);
                                setFromDate(toInputDate(f));
                                setToDate(toInputDate(t));
                            }
                        },
                    ].map(({ label, fn }) => (
                        <button
                            key={label}
                            onClick={fn}
                            className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-600 transition font-medium whitespace-nowrap"
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Chart body ── */}
            <div className="flex-1 px-3 py-1 min-h-0 overflow-y-auto custom-scrollbar">
                {loading ? (
                    <div className="h-full flex flex-col items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
                        <p className="text-xs text-gray-400">Loading…</p>
                    </div>
                ) : !chartOptions ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                        <FiAlertCircle className="text-3xl mb-2 opacity-40" />
                        <p className="text-sm">No missing out punches in this period</p>
                    </div>
                ) : (
                    <div className="w-full">
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartOptions}
                        />
                    </div>
                )}
            </div>

            {/* ── Modal ── */}
            <AnimatePresence>
                {modal.show && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            transition={{ duration: 0.18 }}
                            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                        >
                            <div className="bg-gradient-to-r from-red-50 to-orange-50 px-5 py-4 border-b border-red-100 flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-gray-800 text-base">{modal.name}</h4>
                                    <p className="text-xs text-red-500 font-medium mt-0.5">
                                        {filterLabel} — {modal.count} missing out punch{modal.count !== 1 ? 'es' : ''}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setModal({ ...modal, show: false })}
                                    className="p-1.5 hover:bg-red-100 rounded-full text-red-500 transition"
                                >
                                    <FiX size={18} />
                                </button>
                            </div>
                            <div className="p-5 max-h-[55vh] overflow-y-auto">
                                <p className="text-sm text-gray-500 mb-3 font-medium">
                                    Missing out punch on{' '}
                                    <span className="text-red-600 font-semibold">{modal.dates.length}</span>{' '}
                                    day{modal.dates.length !== 1 ? 's' : ''}:
                                </p>
                                <div className="grid grid-cols-3 gap-2">
                                    {modal.dates.map((date, i) => (
                                        <div key={i} className="bg-red-50 border border-red-100 px-2 py-2 rounded-lg text-xs text-red-700 font-mono text-center hover:bg-red-100 transition font-semibold">
                                            {date}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-gray-50 px-5 py-3 flex justify-end border-t border-gray-100">
                                <button
                                    onClick={() => setModal({ ...modal, show: false })}
                                    className="px-5 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 transition font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MissingOutPunchChart;
